/**
 * Carries a visitor's PostHog identity from readychek.com into the portal.
 *
 * The marketing site (Wix) and the portal are different root domains, so they
 * cannot share PostHog's cookie. Without help, someone who reads the pricing
 * page and then signs up counts as two strangers, and the funnel from ad to
 * sign-up cannot be drawn. The Wix custom code appends the visitor's ids to
 * every readychek.app link as `#ph_distinct_id=…&ph_session_id=…`; this reads
 * them back so PostHog can bootstrap the same person and session.
 *
 * The ids travel in the hash, not the query, so they never reach a server log.
 * They are stripped from the address bar as soon as they are read, so they do
 * not end up in a bookmark, a shared link, or a later pageview's URL.
 */

const DISTINCT_KEY = 'ph_distinct_id';
const SESSION_KEY = 'ph_session_id';

/**
 * PostHog's anonymous ids are UUIDs. Anything else in the hash is ignored
 * rather than trusted: this value becomes the identity every later event is
 * filed under.
 */
const ID_SHAPE = /^[A-Za-z0-9-]{8,64}$/;

/**
 * The PostHog bootstrap for a hash, or null when it carries no usable handoff.
 * Also returns the hash with the two keys removed, so the caller can put back
 * whatever else was there.
 */
export const readHandoff = (hash) => {
  const raw = (hash || '').replace(/^#/, '');
  if (!raw.includes(DISTINCT_KEY)) {
    return { bootstrap: null, remaining: raw };
  }
  const params = new URLSearchParams(raw);
  const distinctID = params.get(DISTINCT_KEY);
  const sessionID = params.get(SESSION_KEY);
  params.delete(DISTINCT_KEY);
  params.delete(SESSION_KEY);
  const remaining = params.toString();

  if (!distinctID || !ID_SHAPE.test(distinctID)) {
    return { bootstrap: null, remaining };
  }
  const bootstrap = { distinctID };
  if (sessionID && ID_SHAPE.test(sessionID)) {
    bootstrap.sessionID = sessionID;
  }
  return { bootstrap, remaining };
};

/**
 * Reads the handoff off the current address and removes it. Called once at
 * startup, before anything else can rewrite the URL. Never throws.
 */
export const takeHandoff = () => {
  try {
    if (typeof window === 'undefined' || !window.location) {
      return null;
    }
    const { bootstrap, remaining } = readHandoff(window.location.hash);
    if (window.location.hash.includes(DISTINCT_KEY) && window.history?.replaceState) {
      const { pathname, search } = window.location;
      const hash = remaining ? `#${remaining}` : '';
      window.history.replaceState(window.history.state, '', `${pathname}${search}${hash}`);
    }
    return bootstrap;
  } catch (e) {
    return null;
  }
};
